import {
  parseWorkflowScope,
  resolveCanonicalDraftScopeSelection,
  resolveScanCameraId,
  type CanonicalDraftScopeSelection,
} from '../../domain/acceptance/safetyScanReportContext';
import {
  customerScanCameraLabel,
  customerScanZoneLabel,
} from '../../domain/acceptance/safetyScanProductPresentation';
import { formatSiteDateTime, formatSiteDateTimeLocalInput } from '../../domain/siteTime/siteTime';
import {
  resolveReportScopeAreaLabel,
  resolveReportScopeCameraLabel,
  resolveReportScopePatternLabel,
} from './safetyReportFilterCopy';
import { customerFacingAreaLabel } from './workspaceUtils';

export function resolveCanonicalZoneDraft(args: {
  reportZoneId?: string | null;
  canonicalZoneId?: string | null;
  persistedReport?: unknown;
  workflowId?: string | null;
}): string {
  const persisted = args.persistedReport && typeof args.persistedReport === 'object'
    ? args.persistedReport as Record<string, any>
    : null;
  const workflowId = String(
    args.workflowId
    || persisted?.workflow_id
    || persisted?.workflow_anchor?.workflow_id
    || '',
  ).trim();
  return String(
    args.reportZoneId
    || args.canonicalZoneId
    || parseWorkflowScope(workflowId).zone_id
    || '',
  ).trim();
}

export function resolveCanonicalCameraDraft(args: {
  cameraFilter?: string | null;
  persistedReport?: unknown;
  workflowId?: string | null;
  urlCameraId?: string | null;
  reportCameraIds?: string[] | null;
}): string {
  const existing = String(args.cameraFilter || '').trim();
  if (existing) return existing;
  const persisted = args.persistedReport && typeof args.persistedReport === 'object'
    ? args.persistedReport as Record<string, any>
    : null;
  return resolveScanCameraId({
    scopeCameraId: persisted?.workflow_anchor?.scope?.camera_id,
    workflowId: args.workflowId
      || persisted?.workflow_id
      || persisted?.workflow_anchor?.workflow_id,
    filterCameraId: args.urlCameraId || (Array.isArray(args.reportCameraIds) ? args.reportCameraIds[0] : ''),
  });
}

export function planCanonicalProofScopeApply(args: {
  persistedReport?: unknown;
  workflowId?: string | null;
  reportId?: string | null;
  zoneDraft?: string | null;
  policyDraft?: string | null;
  cameraDraft?: string | null;
  windowStartDraft?: string | null;
  windowEndDraft?: string | null;
  windowEdited?: boolean;
  siteTimeZone?: string | null;
  urlCameraId?: string | null;
  searchParams: URLSearchParams;
  selectedReviewIncidentIds?: string[];
  proofScopeCaseDraft?: string | null;
  safetyReportTypeMode?: string | null;
  hazardClassKey?: string | null;
}): { canApply: false } | {
  canApply: true;
  resolved: CanonicalDraftScopeSelection;
  nextSearchParams: URLSearchParams;
  fromLocal: string;
  toLocal: string;
} {
  const resolved = resolveCanonicalDraftScopeSelection({
    persistedReport: args.persistedReport,
    workflowId: args.workflowId,
    reportId: args.reportId,
    zoneDraft: args.zoneDraft,
    policyDraft: args.policyDraft,
    cameraDraft: args.cameraDraft,
    windowStartDraft: args.windowStartDraft,
    windowEndDraft: args.windowEndDraft,
    windowEdited: args.windowEdited,
    siteTimeZone: args.siteTimeZone,
    urlCameraId: args.urlCameraId,
  });
  if (!resolved.canApply || !resolved.cameraId) {
    return { canApply: false };
  }
  const rejectReportIdentity = (value: string) => {
    const id = String(value || '').trim();
    return id && id !== String(args.reportId || '').trim() ? id : '';
  };
  const nextIncidentIds = (args.selectedReviewIncidentIds || [])
    .map((value) => rejectReportIdentity(value))
    .filter(Boolean);
  const nextCaseId = String(
    nextIncidentIds[0]
    || rejectReportIdentity(String(args.proofScopeCaseDraft || ''))
    || '',
  ).trim();
  const next = new URLSearchParams(args.searchParams);
  if (resolved.zoneId) next.set('zone_id', resolved.zoneId);
  else next.delete('zone_id');
  if (resolved.policyType) next.set('policy_type', resolved.policyType);
  else next.delete('policy_type');
  next.set('camera_id', resolved.cameraId);
  if (resolved.windowStart) next.set('report_from', resolved.windowStart);
  else next.delete('report_from');
  if (resolved.windowEnd) next.set('report_to', resolved.windowEnd);
  else next.delete('report_to');
  if (args.safetyReportTypeMode === 'hazard_class') {
    next.set('report_type', 'hazard_class');
    next.set('hazard_class', String(args.hazardClassKey || ''));
    next.delete('case_id');
    next.delete('caseId');
    next.delete('incident_id');
    next.delete('incident_ids');
  } else {
    next.delete('report_type');
    next.delete('hazard_class');
    if (nextCaseId) {
      next.set('case_id', nextCaseId);
      next.set('caseId', nextCaseId);
      next.set('incident_id', nextCaseId);
    } else {
      next.delete('case_id');
      next.delete('caseId');
      next.delete('incident_id');
    }
    if (nextIncidentIds.length > 0) next.set('incident_ids', nextIncidentIds.join(','));
    else next.delete('incident_ids');
  }
  next.delete('proof_scope_id');
  next.delete('included_incident_cases');
  next.set('surface', 'package');
  return {
    canApply: true,
    resolved,
    nextSearchParams: next,
    fromLocal: formatSiteDateTimeLocalInput(resolved.windowStart, args.siteTimeZone),
    toLocal: formatSiteDateTimeLocalInput(resolved.windowEnd, args.siteTimeZone),
  };
}

export function formatUpdatedScanScopeLabels(args: {
  persistedReport?: unknown;
  workflowId?: string | null;
  reportId?: string | null;
  zoneDraft?: string | null;
  policyDraft?: string | null;
  cameraDraft?: string | null;
  windowStartDraft?: string | null;
  windowEndDraft?: string | null;
  windowEdited?: boolean;
  siteTimeZone?: string | null;
  urlCameraId?: string | null;
  canonicalWindowStart?: string | null;
  canonicalWindowEnd?: string | null;
  fallbackZoneId?: string | null;
  fallbackCameraId?: string | null;
  fallbackCameraLabel?: string | null;
  patternLabel?: string | null;
  reportBuilding?: boolean;
}): {
  updatedScopeStartLabel: string;
  updatedScopeEndLabel: string;
  zoneLabel: string;
  cameraLabel: string;
  riskPatternLabel: string;
} {
  const resolved = resolveCanonicalDraftScopeSelection({
    persistedReport: args.persistedReport,
    workflowId: args.workflowId,
    reportId: args.reportId,
    zoneDraft: args.zoneDraft,
    policyDraft: args.policyDraft,
    cameraDraft: args.cameraDraft,
    windowStartDraft: args.windowStartDraft,
    windowEndDraft: args.windowEndDraft,
    windowEdited: args.windowEdited,
    siteTimeZone: args.siteTimeZone,
    urlCameraId: args.urlCameraId,
  });
  const startIso = resolved.windowStart || args.canonicalWindowStart || '';
  const endIso = resolved.windowEnd || args.canonicalWindowEnd || '';
  const zoneId = resolved.zoneId || args.zoneDraft || args.fallbackZoneId || '';
  const cameraId = resolved.cameraId || args.cameraDraft || args.fallbackCameraId || '';
  return {
    updatedScopeStartLabel: formatSiteDateTime(startIso, args.siteTimeZone) || 'Not set',
    updatedScopeEndLabel: formatSiteDateTime(endIso, args.siteTimeZone) || 'Not set',
    zoneLabel: customerScanZoneLabel(zoneId)
      || resolveReportScopeAreaLabel(customerFacingAreaLabel(zoneId) || zoneId, Boolean(args.reportBuilding)),
    cameraLabel: customerScanCameraLabel(cameraId)
      || resolveReportScopeCameraLabel(args.fallbackCameraLabel || cameraId, Boolean(args.reportBuilding)),
    riskPatternLabel: resolveReportScopePatternLabel(args.patternLabel || '', Boolean(args.reportBuilding)),
  };
}
